import React, { useState, useEffect, useMemo } from 'react';
import { Swords, Trophy, Grid2x2Plus, User, Menu, SquareUser } from 'lucide-react';
import styles from './BottomNavBar.module.css';
import { useGameState } from '../../context/GameStateContext';
import { useMetadata } from '../../context/MetadataContext';
import { useGlobalError } from '../../context/GlobalErrorContext';

const BottomNavBar = ( ) => {
    const [showMenu, setShowMenu] = useState(false);
    const [visibleItems, setVisibleItems] = useState([]);
    const [menuItems, setMenuItems] = useState([]);
    const { activeMode, changeMode } = useGameState();
    const { metadata } = useMetadata();
    const { triggerCriticalError } = useGlobalError();


    // Get window dimensions for responsive adjustments
    const [windowWidth, setWindowWidth] = useState(window.innerWidth);
    const [windowHeight, setWindowHeight] = useState(window.innerHeight);

    // Use useMemo to prevent unnecessary re-renders
    const modes = useMemo(() => metadata?.modes || [], [metadata?.modes]);

    useEffect(() => {
      if (metadata && modes.length === 0) {
        triggerCriticalError({
          type: 'critical',
          code: 'NO_MODES',
          message: metadata?.messages?.no_modes || 'No game modes are available. Please try again later.',
        });
      }
    }, [metadata, modes.length, triggerCriticalError]);
    
    // Update dimensions and visible items on resize or when modes change
    useEffect(() => {
      const handleResize = () => {
        setWindowWidth(window.innerWidth);
        setWindowHeight(window.innerHeight);
        
        if (!modes || modes.length === 0) return;
        
        // Calculate how many items we can fit
        // For narrow screens, show 3 items + More menu
        // For wider screens, show all items if possible
        const isMobile = window.innerWidth < 768;
        
        if (isMobile) {
          // On mobile, always show first 3 modes and put the rest in the menu
          setVisibleItems(modes.slice(0, 3));
          setMenuItems(modes.slice(3));
        } else {
          // On desktop, show all modes if possible
          if (modes.length <= 5) {
            setVisibleItems(modes);
            setMenuItems([]);
          } else {
            // If more than 5 modes, show first 5 and put the rest in the menu
            setVisibleItems(modes.slice(0, 5));
            setMenuItems(modes.slice(5));
          }
        }
      };
      
      handleResize(); // Call once immediately
      window.addEventListener('resize', handleResize);
      return () => window.removeEventListener('resize', handleResize);
    }, [modes]);
    
    // Determine if we're in portrait mode
    const isPortrait = windowHeight > windowWidth;
    
    // Calculate optimal height based on screen size - but don't go too small
    const navBarHeight = isPortrait 
      ? (windowHeight < 600 ? '52px' : '60px') 
      : '64px';
    
    // Calculate icon size based on screen size - ensure minimum size
    const iconSize = isPortrait 
      ? (windowHeight < 600 ? 20 : 22) 
      : 24;
  
    // Handle mode selection
    const handleModeSelect = (modeCode) => {
      setShowMenu(false);
      changeMode(modeCode);
    };
  
    // Map mode codes to icons with dynamic sizing
    const getModeIcon = (modeCode) => {
      switch(modeCode) {
        case 'solo': return <User size={iconSize} />;
        case 'duel': return <Swords size={iconSize} />;
        case 'tournament': return <Trophy size={iconSize} />;
        case 'create': return <Grid2x2Plus size={iconSize} />;
        default: return <SquareUser size={iconSize} />;
      }
    };
  
    return (
      <nav className={styles.bottomNavBar} style={{ height: navBarHeight }}>
        {visibleItems.map(mode => (
          <div
            key={mode.code}
            className={`${styles.navItem} ${activeMode === mode.code ? styles.active : ''}`}
            onClick={() => handleModeSelect(mode.code)}
            data-tooltip={mode.tooltip || mode.name}
          >
            <div className={styles.navIcon}>
              {getModeIcon(mode.code)}
            </div>
            <div className={`${styles.navLabel} ${isPortrait ? styles.portraitLabel : ''}`}>
              {mode.name}
            </div>
          </div>
        ))}
        
        {menuItems.length > 0 && (
          <div className={`${styles.navItem} ${styles.menuTrigger}`} onClick={() => setShowMenu(!showMenu)}>
            <div className={styles.navIcon} style={{ marginBottom: isPortrait ? '3px' : '4px' }}>
              <Menu size={iconSize} />
            </div>
            <div className={styles.navLabel} style={{ 
              fontSize: isPortrait ? '11px' : '12px',
              visibility: 'visible', // Ensure visibility
              display: 'block' // Ensure display
            }}>
              More
            </div>
            
            {showMenu && (
              <div className={styles.navMenu}>
                {menuItems.map(mode => (
                  <div
                    key={mode.code}
                    className={`${styles.menuItem} ${activeMode === mode.code ? styles.active : ''}`}
                    onClick={() => handleModeSelect(mode.code)}
                    title={mode.tooltip || mode.name}
                  >
                    <div className={styles.menuIcon}>
                      {getModeIcon(mode.code)}
                    </div>
                    <div className={styles.menuLabel}>
                      {mode.name}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}
      </nav>
    );
  };
  
export default BottomNavBar;
